
import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Input,
  Button,
  Table,
  Spinner,
  Collapse,
  Badge,
} from "reactstrap";
import api from "../../api";
import coinImg from "../../assets/images/items/coin.png";
import "./BoxRater.css";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from "recharts";

const VARIANTS = [
  "Diamond",
  "Golden",
  "Ruby",
  "Sapphire",
  "Emerald",
  "Shiny",
  "Shadow",

  "Normal",
];

const VARIANT_COLORS = {
  Diamond: "#4dd0e1",
  Golden: "#fbc02d",
  Ruby: "#e57373",
  Sapphire: "#64b5f6",
  Emerald: "#81c784",
  Shiny: "#b39ddb",
  Shadow: "#37474f", // darker for better key contrast
  Ancient: "#a1887f",
  Normal: "#ffffff",
  Total: "#ff9800",
};

function formatCoins(n) {
  if (typeof n !== "number") return n;
  return n.toLocaleString();
}

// Compact number formatter for coin values: 20k, 1.2m
function formatShort(n) {
  if (typeof n !== "number") return n;
  const abs = Math.abs(n);
  if (abs >= 1_000_000) {
    const v = n / 1_000_000;
    const s = Number.isInteger(v) ? v.toFixed(0) : v.toFixed(1);
    return `${s}m`;
  }
  if (abs >= 1_000) {
    const v = n / 1_000;
    const s = Number.isInteger(v) ? v.toFixed(0) : v.toFixed(1);
    return `${s}k`;
  }
  return n.toLocaleString();
}

function SummaryView({ data, loading, error }) {
  const [open, setOpen] = useState({});
  const toggle = (v) => setOpen((prev) => ({ ...prev, [v]: !prev[v] }));
  const details = data?.details || {};

  const genderIcon = (g) => {
    if (!g) return "—";
    if (g === "M") return "♂";
    if (g === "F") return "♀";
    return "—";
  };
  if (loading) return <Spinner color="light" />;
  if (error) return <div className="text-danger">{error}</div>;
  if (!data) return null;

  const { total_count, total_value, variants } = data;
  const totalValSafe = Math.max(1, total_value || 0);
  const percentRow = { name: "Share" };
  VARIANTS.forEach((v) => {
    percentRow[v] = ((variants?.[v]?.value || 0) / totalValSafe) * 100;
  });

  return (
    <Row>
      <Col md={12} className="mb-3">
        <Card className="boxrater-card">
          <CardBody>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-2 text-white">Totals</h5>
              <Badge color="info">Summary</Badge>
            </div>
            <div className="small boxrater-overview">Current box overview</div>
            <div className="mt-2 boxrater-emphasis">Pokémon: <strong>{formatCoins(total_count)}</strong></div>
            <div className="boxrater-emphasis">Box Value: <img src={coinImg} alt="coin" className="boxrater-coin" /><strong>{formatShort(total_value)}</strong></div>

            {/* Stacked percentage bar */}
            <div className="mt-3 boxrater-sharebar">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-muted">Variant distribution</small>
              </div>
              <div style={{ width: "100%", height: 36 }}>
                <ResponsiveContainer>
                  <BarChart data={[percentRow]} layout="vertical" margin={{ top: 0, right: 8, bottom: 0, left: 0 }}>
                    <XAxis type="number" domain={[0, 100]} hide tickFormatter={(v) => `${Math.round(v)}%`} />
                    <YAxis type="category" dataKey="name" hide />
                    <Tooltip formatter={(v, name) => [`${v.toFixed(1)}%`, name]} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {VARIANTS.map((v) => (
                      <Bar key={v} dataKey={v} stackId="share" fill={VARIANT_COLORS[v]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </CardBody>
        </Card>
      </Col>

      {VARIANTS.map((v) => (
        <Col xs={12} md={open[v] ? 12 : 6} lg={open[v] ? 12 : 4} key={v} className="mb-3">
          <Card className="boxrater-card boxrater-variant-card" style={{ '--variant-color': VARIANT_COLORS[v] }}>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-2 variant-header">
                <h6 className="mb-0 boxrater-variant-title"><span className="boxrater-variant-dot" style={{ backgroundColor: VARIANT_COLORS[v] }} />{v}</h6>
                <Button size="sm" color="secondary" outline className="boxrater-toggle" onClick={() => toggle(v)}>
                  {open[v] ? "Hide details" : "Show details"}
                </Button>
              </div>
              <div className="mb-2 boxrater-variant-stats">
                <span className="me-3">Count: <strong>{formatCoins(variants?.[v]?.count || 0)}</strong></span>
                <span>Value: <img src={coinImg} alt="coin" className="boxrater-coin" /><strong>{formatShort(variants?.[v]?.value || 0)}</strong></span>
              </div>

              <Collapse isOpen={!!open[v]}>
                <div className="boxrater-details">
                  <Table responsive dark striped size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: "40%" }}>Species</th>
                        <th style={{ width: "10%" }}>Gender</th>
                        <th style={{ width: "15%" }} className="text-end"><img src={coinImg} alt="coin" className="boxrater-coin" />Rate List</th>
                        <th style={{ width: "15%" }} className="text-end">Count</th>
                        <th style={{ width: "20%" }} className="text-end"><img src={coinImg} alt="coin" className="boxrater-coin" />Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(details[v] || []).length === 0 ? (
                        <tr>
                          <td colSpan={5} className="text-center text-muted">No Pokémon in this variant</td>
                        </tr>
                      ) : (
                        (details[v] || []).map((row, idx) => (
                          <tr key={idx}>
                            <td>{row.species}</td>
                            <td>{genderIcon(row.gender)}</td>
                            <td className="text-end"><img src={coinImg} alt="coin" className="boxrater-coin" />{formatShort(row.unit_price)}</td>
                            <td className="text-end">{formatCoins(row.count)}</td>
                            <td className="text-end"><img src={coinImg} alt="coin" className="boxrater-coin" />{formatShort(row.total_value)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </Collapse>
            </CardBody>
          </Card>
        </Col>
      ))}
    </Row>
  );
}

function CompareView({ onCompare, loading, error, data }) {
  const [other, setOther] = useState("");
  const CompareTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <div className="recharts-default-tooltip">
        <div><strong>{label}</strong></div>
        {payload.map((p, idx) => (
          <div key={idx}>{p.name}: <img src={coinImg} alt="coin" className="boxrater-coin" />{formatShort(typeof p.value === 'number' ? p.value : Number(p.value) || 0)}</div>
        ))}
      </div>
    );
  };
  const compareChartData = useMemo(() => {
    if (!data) return [];
    const rows = VARIANTS.map((v) => ({
      variant: v,
      userValue: data.user?.variants?.[v]?.value || 0,
      otherValue: data.other?.variants?.[v]?.value || 0,
    }));
    return [
      {
        variant: "Total",
        userValue: data.user?.total_value || 0,
        otherValue: data.other?.total_value || 0,
      },
      ...rows,
    ];
  }, [data]);

  return (
    <>
      <Row className="mb-3">
        <Col md={8} lg={6} xl={4}>
          <Input
            className="boxrater-input"
            placeholder="Other username"
            value={other}
            onChange={(e) => setOther(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onCompare(other);
            }}
          />
        </Col>
        <Col md="auto">
          <Button color="primary" onClick={() => onCompare(other)} disabled={!other || loading}>
            {loading ? "Comparing..." : "Compare"}
          </Button>
        </Col>
      </Row>

      

      {error && <div className="text-danger mb-2">{error}</div>}

      {data && (
        <Row>
          <Col md={12} className="mb-3">
            <Card className="boxrater-card">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-3 text-white">Totals</h5>
                  <Badge color="primary">Compare</Badge>
                </div>
                <Row>
                  <Col md={6}>
                    <div className="boxrater-emphasis"><strong>{data.user.username}</strong></div>
                    <div className="boxrater-emphasis">Pokémon: {formatCoins(data.user.total_count)}</div>
                    <div className="boxrater-emphasis">Box Value: <img src={coinImg} alt="coin" className="boxrater-coin" />{formatShort(data.user.total_value)}</div>
                  </Col>
                  <Col md={6}>
                    <div className="boxrater-emphasis"><strong>{data.other.username}</strong></div>
                    <div className="boxrater-emphasis">Pokémon: {formatCoins(data.other.total_count)}</div>
                    <div className="boxrater-emphasis">Box Value: <img src={coinImg} alt="coin" className="boxrater-coin" />{formatShort(data.other.total_value)}</div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>

          <Col md={12} className="mb-3">
            <Card className="boxrater-card">
              <CardBody>
                <h6 className="mb-3">Variant Value Comparison</h6>
                <div style={{ width: "100%", height: 340 }}>
                  <ResponsiveContainer>
                    <BarChart data={compareChartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#3f4853" />
                      <XAxis dataKey="variant" tick={{ fill: '#e8eaed', fontSize: 12 }} />
                      <YAxis tick={{ fill: '#e8eaed', fontSize: 12 }} tickFormatter={(v) => formatShort(v)} />
                      <Tooltip content={<CompareTooltip />} />
                      <Legend />
                      <Bar dataKey="userValue" name={data.user.username} fill="#42a5f5" />
                      <Bar dataKey="otherValue" name={data.other.username} fill="#ef5350" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col md={12}>
            <Card className="boxrater-card">
              <CardBody>
                <h6 className="mb-3">Per Variant</h6>
                <Table responsive striped dark className="boxrater-table">
                  <thead>
                    <tr>
                      <th>Variant</th>
                      <th className="text-end">{data.user.username} Count</th>
                      <th className="text-end"><img src={coinImg} alt="coin" className="boxrater-coin" />{data.user.username} Value</th>
                      <th className="text-end">{data.other.username} Count</th>
                      <th className="text-end"><img src={coinImg} alt="coin" className="boxrater-coin" />{data.other.username} Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {VARIANTS.map((v) => (
                      <tr key={v}>
                        <td>{v}</td>
                        <td className="text-end">{formatCoins(data.user.variants?.[v]?.count || 0)}</td>
                        <td className="text-end"><img src={coinImg} alt="coin" className="boxrater-coin" />{formatShort(data.user.variants?.[v]?.value || 0)}</td>
                        <td className="text-end">{formatCoins(data.other.variants?.[v]?.count || 0)}</td>
                        <td className="text-end"><img src={coinImg} alt="coin" className="boxrater-coin" />{formatShort(data.other.variants?.[v]?.value || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
}

function GrowthView({ data, loading, error, weeks, onWeeksChange }) {
  const [selectedSeries, setSelectedSeries] = useState(() => new Set(["Total"]));

  const chartData = useMemo(() => {
    if (!data) return [];
    // Build a chart row per week with variant values + total
    const byWeek = {};
    // per variant series
    for (const v of Object.keys(data.series || {})) {
      for (const p of data.series[v]) {
        const wk = p.week;
        if (!byWeek[wk]) byWeek[wk] = { week: wk };
        byWeek[wk][v] = (byWeek[wk][v] || 0) + (p.value || 0);
      }
    }
    // totals
    for (const t of data.totals || []) {
      const wk = t.week;
      if (!byWeek[wk]) byWeek[wk] = { week: wk };
      byWeek[wk].Total = t.value || 0;
    }
    return Object.values(byWeek).sort((a, b) => a.week.localeCompare(b.week));
  }, [data]);

  const toggleSeries = (key) => {
    setSelectedSeries((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      if (next.size === 0) next.add("Total");
      return next;
    });
  };

  const selectAll = () => setSelectedSeries(new Set(["Total", ...VARIANTS]));
  const clearAll = () => setSelectedSeries(new Set(["Total"]));

  const GrowthTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0]?.payload || {};
    return (
      <div className="recharts-default-tooltip">
        <div><strong>Week: {label}</strong></div>
        <div>Total: <img src={coinImg} alt="coin" className="boxrater-coin" />{formatShort(d.Total || 0)}</div>
        {VARIANTS.map((v) => (
          <div key={v}>{v}: <img src={coinImg} alt="coin" className="boxrater-coin" />{formatShort(d[v] || 0)}</div>
        ))}
      </div>
    );
  };

  return (
    <>
      <Row className="mb-3">
        <Col md="auto">
          <label className="me-2 text-white">Range:</label>
          <Button
            size="sm"
            color={weeks === 4 ? "primary" : "secondary"}
            className="me-1"
            onClick={() => onWeeksChange(4)}
          >
            4w
          </Button>
          <Button
            size="sm"
            color={weeks === 12 ? "primary" : "secondary"}
            className="me-1"
            onClick={() => onWeeksChange(12)}
          >
            12w
          </Button>
          <Button
            size="sm"
            color={weeks === 24 ? "primary" : "secondary"}
            className="me-1"
            onClick={() => onWeeksChange(24)}
          >
            24w
          </Button>
          <Button
            size="sm"
            color={weeks === 52 ? "primary" : "secondary"}
            onClick={() => onWeeksChange(52)}
          >
            52w
          </Button>
        </Col>
      </Row>

      {loading && <Spinner color="light" />} 
      {error && <div className="text-danger">{error}</div>}

      {!loading && data && (
        <Row>
          <Col md={12} className="mb-2">
            <div className="d-flex flex-wrap align-items-center">
              <label className="me-2 mb-1 text-white">Series:</label>
              <Button size="sm" color={selectedSeries.has("Total") ? "primary" : "secondary"} className="me-1 mb-1" onClick={() => toggleSeries("Total")}>
                Total
              </Button>
              {VARIANTS.map((v) => (
                <Button
                  key={v}
                  size="sm"
                  outline
                  className="me-1 mb-1"
                  style={{ borderColor: VARIANT_COLORS[v], color: selectedSeries.has(v) ? "#fff" : VARIANT_COLORS[v], background: selectedSeries.has(v) ? "rgba(255,255,255,0.08)" : "transparent" }}
                  color={selectedSeries.has(v) ? "primary" : "secondary"}
                  onClick={() => toggleSeries(v)}
                >
                  {v}
                </Button>
              ))}
              <Button size="sm" color="secondary" className="ms-1 mb-1" onClick={selectAll}>All</Button>
              <Button size="sm" color="secondary" className="ms-1 mb-1" onClick={clearAll}>None</Button>
            </div>
          </Col>
          <Col md={12} className="mb-3">
            <Card className="boxrater-card">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-3 text-white">Total Box Value Over Time</h6>
                  <Badge color="warning">Growth</Badge>
                </div>
                <div style={{ width: "100%", height: 320 }}>
                  <ResponsiveContainer>
                    <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#3f4853" />
                      <XAxis dataKey="week" tick={{ fill: '#e8eaed', fontSize: 12 }} />
                      <YAxis tick={{ fill: '#e8eaed', fontSize: 12 }} tickFormatter={(v) => formatShort(v)} width={80} />
                      <Tooltip content={<GrowthTooltip />} />
                      <Legend />
                      {[...selectedSeries]
                        .sort((a, b) => (a === "Total" ? -1 : b === "Total" ? 1 : VARIANTS.indexOf(a) - VARIANTS.indexOf(b)))
                        .map((key) => (
                          <Line
                            type="monotone"
                            key={key}
                            dataKey={key}
                            stroke={VARIANT_COLORS[key] || '#90caf9'}
                            dot={{ r: 2 }}
                            strokeWidth={2}
                          />
                        ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col md={12}>
            <Card className="boxrater-card">
              <CardBody>
                <h6 className="mb-3 text-white">Per Variant (latest week)</h6>
                <Table responsive striped dark className="boxrater-table">
                  <thead>
                    <tr>
                      <th>Variant</th>
                      <th className="text-end">Count</th>
                      <th className="text-end"><img src={coinImg} alt="coin" className="boxrater-coin" />Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      // find last week in series
                      const keys = Object.keys(data.series || {});
                      if (!keys.length) return null;
                      // assume all series share last week label when snapshots exist
                      let lastWeek = null;
                      for (const v of keys) {
                        const arr = data.series[v];
                        if (arr && arr.length) {
                          const wk = arr[arr.length - 1].week;
                          if (!lastWeek || wk > lastWeek) lastWeek = wk;
                        }
                      }
                      return VARIANTS.map((v) => {
                        const arr = data.series[v] || [];
                        const rec = (arr || []).find((p) => p.week === lastWeek) || { count: 0, value: 0 };
                        return (
                          <tr key={v}>
                            <td>{v}</td>
                            <td className="text-end">{formatCoins(rec.count || 0)}</td>
                            <td className="text-end"><img src={coinImg} alt="coin" className="boxrater-coin" />{formatShort(rec.value || 0)}</td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
}

export default function BoxRater() {
  const [activeTab, setActiveTab] = useState("summary");

  // Summary
  const [summary, setSummary] = useState(null);
  const [sumLoading, setSumLoading] = useState(false);
  const [sumError, setSumError] = useState("");

  // Compare
  const [compareData, setCompareData] = useState(null);
  const [compLoading, setCompLoading] = useState(false);
  const [compError, setCompError] = useState("");

  // Growth
  const [weeks, setWeeks] = useState(12);
  const [growth, setGrowth] = useState(null);
  const [growLoading, setGrowLoading] = useState(false);
  const [growError, setGrowError] = useState("");

  useEffect(() => {
    document.title = "Box Rater";
  }, []);

  useEffect(() => {
    // Fetch summary once on mount or when tab becomes active if not loaded
    if (activeTab === "summary" && !summary && !sumLoading) {
      (async () => {
        setSumLoading(true);
        setSumError("");
        try {
          const res = await api.get("/v1/box-rater/summary", { withCredentials: true });
          setSummary(res.data);
        } catch (e) {
          console.error(e);
          setSumError("Failed to load summary.");
        } finally {
          setSumLoading(false);
        }
      })();
    }
  }, [activeTab, summary, sumLoading]);

  useEffect(() => {
    // Fetch growth when tab active or when weeks changes
    if (activeTab === "growth") {
      (async () => {
        setGrowLoading(true);
        setGrowError("");
        try {
          const res = await api.get(`/v1/box-rater/growth?weeks=${weeks}`, { withCredentials: true });
          setGrowth(res.data);
        } catch (e) {
          console.error(e);
          setGrowError("Failed to load growth data.");
        } finally {
          setGrowLoading(false);
        }
      })();
    }
  }, [activeTab, weeks]);

  const doCompare = async (otherUsername) => {
    setCompLoading(true);
    setCompError("");
    setCompareData(null);
    try {
      const res = await api.get(`/v1/box-rater/compare`, {
        withCredentials: true,
        params: { other_username: otherUsername },
      });
      setCompareData(res.data);
    } catch (e) {
      console.error(e);
      setCompError(e?.response?.data?.detail || "Failed to compare.");
    } finally {
      setCompLoading(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col md={12}>
            <Card className="boxrater-card">
              <CardBody>
                <h4 className="mb-3">Box Rater</h4>
                <Nav tabs>
                  <NavItem>
                    <NavLink
                      className={activeTab === "summary" ? "active" : ""}
                      onClick={() => setActiveTab("summary")}
                      style={{ cursor: "pointer" }}
                    >
                      Summary
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === "compare" ? "active" : ""}
                      onClick={() => setActiveTab("compare")}
                      style={{ cursor: "pointer" }}
                    >
                      Compare
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === "growth" ? "active" : ""}
                      onClick={() => setActiveTab("growth")}
                      style={{ cursor: "pointer" }}
                    >
                      Growth
                    </NavLink>
                  </NavItem>
                </Nav>

                <TabContent activeTab={activeTab} className="pt-3">
                  <TabPane tabId="summary">
                    <SummaryView data={summary} loading={sumLoading} error={sumError} />
                  </TabPane>
                  <TabPane tabId="compare">
                    <CompareView onCompare={doCompare} loading={compLoading} error={compError} data={compareData} />
                  </TabPane>
                  <TabPane tabId="growth">
                    <GrowthView
                      data={growth}
                      loading={growLoading}
                      error={growError}
                      weeks={weeks}
                      onWeeksChange={setWeeks}
                    />
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
